import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  writeBatch,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const DataCorrectionTool = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchMonth, setSearchMonth] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newPercentage, setNewPercentage] = useState(0);

  useEffect(() => {
    loadMonthlyData();
  }, []);

  const loadMonthlyData = async () => {
    setLoading(true);
    try {
      const q = query(collection(DATABASE, "monthly_grpahs"), orderBy("graph_month_date", "desc"));
      const querySnapshot = await getDocs(q);
      
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().graph_month_date?.toDate?.() || doc.data().created_at?.toDate?.() || new Date(),
      }));

      // Enrich data with user information
      const enrichedData = await Promise.all(data.map(async (item) => {
        const userId = item.userID || item.user_id || item.userDocId || item.docId;
        
        if (userId) {
          try {
            const userDoc = await getDocs(
              query(collection(DATABASE, "users"), where("__name__", "==", userId))
            );
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              return {
                ...item,
                userEmail: userData.email || "Unknown Email",
                userName: userData.full_name || "Unknown User",
              };
            }
          } catch (error) {
            console.error("Error fetching user data:", error);
          }
        }
        
        return {
          ...item,
          userEmail: "Unknown Email",
          userName: "Unknown User",
        };
      }));
      
      setMonthlyData(enrichedData);
    } catch (error) {
      console.error("Error loading monthly data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load monthly graphs data",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = monthlyData.filter((item) => {
    const emailMatch = !searchEmail || item.userEmail?.toLowerCase().includes(searchEmail.toLowerCase());
    const monthMatch = !searchMonth || 
      (item.graph_month !== undefined && item.graph_year !== undefined && 
       dayjs().month(item.graph_month).year(item.graph_year).format("MMMM YYYY").toLowerCase().includes(searchMonth.toLowerCase()));
    return emailMatch && monthMatch;
  });

  const handleEditPercentage = (item) => {
    setEditingItem(item);
    setNewPercentage(item.total_Monthly_percantage || 0);
    setEditDialogOpen(true);
  };

  const handleSavePercentage = async () => {
    if (!editingItem) return;

    try {
      const batch = writeBatch(DATABASE);
      
      // Update the monthly graph entry
      const monthlyGraphRef = doc(DATABASE, "monthly_grpahs", editingItem.id);
      batch.update(monthlyGraphRef, {
        total_Monthly_percantage: parseFloat(newPercentage),
        updated_at: new Date(),
        correction_note: `Percentage corrected from ${editingItem.total_Monthly_percantage} to ${newPercentage}`
      });

      // Also update related entries if they exist
      // Update monthly_closings
      const monthlyClosingsQuery = query(
        collection(DATABASE, "monthly_closings"),
        where("user_id", "==", editingItem.userID || editingItem.user_id),
        where("closing_month", "==", editingItem.graph_month),
        where("closing_year", "==", editingItem.graph_year)
      );
      const monthlyClosingsSnapshot = await getDocs(monthlyClosingsQuery);
      
      monthlyClosingsSnapshot.docs.forEach((doc) => {
        const closingData = doc.data();
        const newProfitLoss = (closingData.closing_balance - closingData.profit_loss) * (parseFloat(newPercentage) / 100);
        const newClosingBalance = (closingData.closing_balance - closingData.profit_loss) + newProfitLoss;
        
        batch.update(doc.ref, {
          percent: parseFloat(newPercentage),
          profit_loss: newProfitLoss,
          closing_balance: newClosingBalance,
          updated_at: new Date(),
          correction_note: `Percentage corrected from ${editingItem.total_Monthly_percantage} to ${newPercentage}`
        });
      });

      // Update ledgers
      const ledgersQuery = query(
        collection(DATABASE, "ledgers"),
        where("user_id", "==", editingItem.userID || editingItem.user_id),
        where("transection_type", "==", 3)
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      
      ledgersSnapshot.docs.forEach((doc) => {
        const ledgerData = doc.data();
        const newProfitLoss = (ledgerData.previous_balance * parseFloat(newPercentage)) / 100;
        const newCurrentBalance = ledgerData.previous_balance + newProfitLoss;
        
        batch.update(doc.ref, {
          profit_loss_percent: parseFloat(newPercentage),
          profit_loss_balance: newProfitLoss,
          new_txn_amount: newProfitLoss,
          current_balance: newCurrentBalance,
          updated_at: new Date(),
          correction_note: `Percentage corrected from ${editingItem.total_Monthly_percantage} to ${newPercentage}`
        });
      });

      // Update transaction requests
      const transactionsQuery = query(
        collection(DATABASE, "transection_requests"),
        where("user_id", "==", editingItem.userID || editingItem.user_id),
        where("request_type", "==", 2)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      transactionsSnapshot.docs.forEach((doc) => {
        const transactionData = doc.data();
        const newProfitLoss = (transactionData.remaining_balance * parseFloat(newPercentage)) / 100;
        
        batch.update(doc.ref, {
          profit_loss_percent: parseFloat(newPercentage),
          profit_loss: newProfitLoss,
          updated_at: new Date(),
          correction_note: `Percentage corrected from ${editingItem.total_Monthly_percantage} to ${newPercentage}`
        });
      });

      // Update yearly graph
      const yearlyGraphQuery = query(
        collection(DATABASE, "yearly_graph"),
        where("userID", "==", editingItem.userID || editingItem.user_id),
        where("month", "==", editingItem.graph_month + 1),
        where("year", "==", editingItem.graph_year)
      );
      const yearlyGraphSnapshot = await getDocs(yearlyGraphQuery);
      
      yearlyGraphSnapshot.docs.forEach((doc) => {
        batch.update(doc.ref, {
          amount: parseFloat(newPercentage),
          // NO individual_percentage - only use amount field
          updated_at: new Date(),
          correction_note: `Percentage corrected from ${editingItem.total_Monthly_percantage} to ${newPercentage}`
        });
      });

      await batch.commit();

      Swal.fire({
        icon: "success",
        title: "Data Correction Successful!",
        html: `
          <div>
            <p><strong>Percentage updated for:</strong></p>
            <p>User: ${editingItem.userEmail}</p>
            <p>Month: ${dayjs().month(editingItem.graph_month).year(editingItem.graph_year).format("MMMM YYYY")}</p>
            <p>Old Percentage: ${editingItem.total_Monthly_percantage}%</p>
            <p>New Percentage: ${newPercentage}%</p>
            <br>
            <p><strong>Updated Collections:</strong></p>
            <ul style="text-align: left; font-size: 12px;">
              <li>monthly_grpahs</li>
              <li>monthly_closings</li>
              <li>ledgers</li>
              <li>transection_requests</li>
              <li>yearly_graph</li>
            </ul>
          </div>
        `,
        width: 600,
      });

      setEditDialogOpen(false);
      setEditingItem(null);
      loadMonthlyData();
    } catch (error) {
      console.error("Error updating percentage:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to update percentage: ${error.message}`,
      });
    }
  };

  const getStatusChip = (item) => {
    if (item.total_Monthly_percantage < 0) {
      return <Chip label="Negative Performance" color="error" size="small" />;
    } else if (item.total_Monthly_percantage > 0) {
      return <Chip label="Positive Performance" color="success" size="small" />;
    } else {
      return <Chip label="No Performance" color="default" size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Data Correction Tool
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Correct percentage values and synchronize data across all collections. 
        This tool will update the percentage in all related database entries.
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {monthlyData.length}
              </Typography>
              <Typography variant="body2">Total Entries</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {monthlyData.filter(item => item.total_Monthly_percantage < 0).length}
              </Typography>
              <Typography variant="body2">Negative Performance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {monthlyData.filter(item => item.total_Monthly_percantage > 0).length}
              </Typography>
              <Typography variant="body2">Positive Performance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {monthlyData.filter(item => item.total_Monthly_percantage === 0).length}
              </Typography>
              <Typography variant="body2">Zero Performance</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search by Email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Search by Month"
          value={searchMonth}
          onChange={(e) => setSearchMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={loadMonthlyData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Current %</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {item.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.userName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.graph_month !== undefined && item.graph_year !== undefined 
                      ? dayjs().month(item.graph_month).year(item.graph_year).format("MMMM YYYY")
                      : "N/A"
                    }
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={item.total_Monthly_percantage < 0 ? "error.main" : item.total_Monthly_percantage > 0 ? "success.main" : "text.secondary"}
                    >
                      {item.total_Monthly_percantage?.toFixed(2) || "0.00"}%
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(item)}</TableCell>
                  <TableCell>
                    {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit Percentage">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditPercentage(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Correct Percentage Value
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingItem && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This will update the percentage value across all related database collections.
                </Typography>
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Entry Details:
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>User:</strong> {editingItem.userEmail}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Month:</strong> {dayjs().month(editingItem.graph_month).year(editingItem.graph_year).format("MMMM YYYY")}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Current %:</strong> {editingItem.total_Monthly_percantage?.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Days Count:</strong> {editingItem.graph_points?.length || 0}</Typography>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="New Percentage Value"
                type="number"
                value={newPercentage}
                onChange={(e) => setNewPercentage(parseFloat(e.target.value) || 0)}
                inputProps={{
                  step: 0.01,
                  min: -100,
                  max: 100,
                }}
                helperText="Enter negative values for losses (e.g., -5.48 for -5.48%)"
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePercentage} variant="contained" color="primary">
            Update Percentage
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataCorrectionTool;
